import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Target, X } from "lucide-react";
import { toast } from "sonner";

const GROUPS = [
  "chest", "shoulders", "biceps", "triceps", "back", "lats",
  "core", "quads", "glutes", "hamstrings", "calves", "forearms",
];

/**
 * WeaknessPicker — coach-side widget for setting "Weakness of the Week" on a
 * single client.  Displays the currently active focus (with metadata) and lets
 * the coach change or clear it.  Meant to live inside ClientDetail.jsx.
 */
export default function WeaknessPicker({ clientId, clientFirstName }) {
  const [current, setCurrent] = useState(null);
  const [muscle, setMuscle] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/coaching/clients/${clientId}/weakness`);
      if (r.data) {
        setCurrent(r.data);
        setMuscle(r.data.muscle_group);
        setNote(r.data.note || "");
      } else {
        setCurrent(null);
        setMuscle("");
        setNote("");
      }
    } catch {
      /* silent */
    }
  };
  useEffect(() => {
    if (clientId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const save = async (e) => {
    e.preventDefault();
    if (!muscle) return toast.error("Pick a muscle group first");
    setSaving(true);
    try {
      await api.put(`/coaching/clients/${clientId}/weakness`, {
        muscle_group: muscle,
        note: note.trim(),
      });
      toast.success(
        `Focus set — ${clientFirstName || "your client"} will see ${muscle} at the top of their dashboard.`,
      );
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Clear this week's focus?")) return;
    try {
      await api.delete(`/coaching/clients/${clientId}/weakness`);
      toast.success("Focus cleared");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to clear");
    }
  };

  return (
    <div className="obs-card" data-testid="weakness-picker">
      <div className="flex items-center gap-3 mb-3">
        <Target className="h-5 w-5 text-blaze" />
        <div className="flex-1">
          <div className="overline">Weakness of the week</div>
          <h3 className="font-heading text-2xl uppercase">Focus muscle</h3>
        </div>
        {current && (
          <button
            type="button"
            onClick={clear}
            className="ghost-btn px-3 py-1 text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5"
            data-testid="weakness-clear"
          >
            <X className="h-3 w-3" /> Clear
          </button>
        )}
      </div>

      {current && (
        <div
          className="border border-blaze/50 bg-blaze/8 rounded-sm p-3 mb-4"
          data-testid="weakness-current"
        >
          <div className="text-[10px] uppercase tracking-widest text-blaze">
            Currently active · set {new Date(current.updated_at || current.set_at).toLocaleDateString()}
          </div>
          <div className="font-heading text-2xl uppercase mt-1">{current.muscle_group}</div>
          {current.note && (
            <p className="text-sm text-zinc-300 mt-2 whitespace-pre-wrap">{current.note}</p>
          )}
        </div>
      )}

      <form onSubmit={save} className="space-y-3">
        <div>
          <label className="overline block mb-1.5">Muscle group</label>
          <div className="flex flex-wrap gap-1.5">
            {GROUPS.map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setMuscle(g)}
                className={`text-[10px] uppercase tracking-widest px-3 py-1 border rounded-full transition-colors ${
                  muscle === g
                    ? "bg-blaze text-obsidian-900 border-blaze"
                    : "border-obsidian-600 text-zinc-400 hover:border-blaze hover:text-blaze"
                }`}
                data-testid={`weakness-group-${g}`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="overline block mb-1.5">Coach note (optional)</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="e.g. Prioritise heavy rows and rear-delt work — you kept skipping pulls last week."
            maxLength={280}
            className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze text-sm"
            data-testid="weakness-note"
          />
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1 text-right">
            {note.length}/280
          </div>
        </div>

        <button
          type="submit"
          disabled={saving || !muscle}
          className="blaze-btn px-4 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="weakness-save"
        >
          <Target className="h-3 w-3" /> {saving ? "Saving…" : current ? "Update focus" : "Set focus"}
        </button>
      </form>
    </div>
  );
}
